import React, { useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { Box, Typography } from "@mui/material";
import { translate } from "../../../../translate/translate";
import DirectionsBoatFilledOutlinedIcon from "@mui/icons-material/DirectionsBoatFilledOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { Analysis, AnalysisResultExtractType, ExtractData, ExtractItem, InTransitExtractData } from "../../../../models/general/analysis";
import { ErrorComponent } from "../../../../components/error/error.component";
import { BaseService } from "../../../../services/base-service";
import { URLS } from "../../../../services/app-urls";
import dayjs from "dayjs";
import {
  CardHeader,
  CellLabel,
  Chip,
  DialogBar,
  ExtractCard,
  Foot,
  Qty,
  Row,
  Rows,
  SummaryRow,
  T,
  formatValue,
} from "./extract-shared";

/** Splits "CODE (PR-1)" into the card name and the badge. */
const splitBadge = (name: string): { label: string; badge: string | null } => {
  const match = name.match(/^(.*?)\s*\((PR-\d+|ALT-\d+)\)\s*$/);
  if (match) return { label: match[1], badge: match[2] };
  return { label: name, badge: null };
};

const DialogResultInTransit = (props: any) => {
  const { open, onClose, title, analysisResultIds } = props;

  const service = new BaseService<Analysis>(URLS.ANALYSIS);
  const [data, setData] = React.useState<InTransitExtractData>();
  const [isProcessingError, setIsProcessingError] = React.useState<boolean>(false);
  const [isDataInTransit, setIsDataInTransit] = React.useState<boolean>(false);

  const handleCloseDialog = () => {
    onClose();
  };

  useEffect(() => {
    getInTransitInfo();
  }, [analysisResultIds]);

  const getInTransitInfo = () => {
    if (analysisResultIds.length === 0) return;
    setIsProcessingError(false);
    setIsDataInTransit(false);
    service.clearParameters();
    service.addParameter("analysis_result_ids", analysisResultIds);
    service.addParameter("extract_type", AnalysisResultExtractType.IN_TRANSIT);
    service
      .getAllFromListRoute("extract")
      .then((response: any) => {
        const responseData = Array.isArray(response?.data) ? response?.data[0] : response?.data;
        setData(responseData);
        const isEmptyResponse =
          responseData?.total === 0 &&
          (responseData?.primary?.length === 0 ||
            responseData?.primary?.every((extractData: ExtractData) => extractData?.group?.items?.length === 0));
        if (isEmptyResponse) {
          setIsDataInTransit(true);
        }
      })
      .catch((error: any) => {
        if (error?.response?.status === 409 || error?.response?.status === 404) {
          setIsProcessingError(true);
        } else {
          ErrorComponent(error);
        }
      });
  };

  const formatDeliveryDate = (date: any) => {
    const d = dayjs(date);
    return d.isValid() ? d.format("L") : "-";
  };

  /** One row per invoice, with ETD. */
  const renderGroup = (group: ExtractData, railColor: string) => {
    const items = group?.group?.items?.filter((item: ExtractItem) => item?.code) || [];
    if (items.length === 0) return null;
    const { label, badge } = splitBadge(group.name);

    return (
      <ExtractCard key={group.name}>
        <CardHeader
          railColor={railColor}
          name={label}
          right={badge ? <Chip variant={badge.startsWith("ALT") ? "alt" : "pr"}>{badge}</Chip> : undefined}
        />
        <Rows>
          {items.map((item: ExtractItem) => (
            <Row
              key={item.code}
              left={
                <>
                  <CellLabel mono>{item.code}</CellLabel>
                  <Chip variant="use">{formatDeliveryDate(item?.estimated_time_delivery)}</Chip>
                </>
              }
              qty={<Qty>{formatValue(item?.quantity)}</Qty>}
            />
          ))}
        </Rows>
        <SummaryRow label={translate.t("subtotal_label")} value={formatValue(group?.group?.total)} />
      </ExtractCard>
    );
  };

  const emptyOrError = isProcessingError || isDataInTransit;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogBar label={translate.t("in_transit")} pn={title} onClose={handleCloseDialog} />

      <DialogContent sx={{ maxHeight: "450px", overflowY: "auto", padding: 0, background: T.surface }}>
        {isProcessingError ? (
          <Box
            sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", gap: 2, minHeight: "300px", width: 380 }}
          >
            <AccessTimeOutlinedIcon sx={{ fontSize: 64, color: "#CFD8DC" }} />
            <Typography sx={{ color: T.inkSoft, fontWeight: 600, textAlign: "center", fontSize: "16px", maxWidth: "340px" }}>
              {translate.t("cell_data_processing")}
            </Typography>
          </Box>
        ) : isDataInTransit ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", padding: "80px", width: 380 }}>
            <DirectionsBoatFilledOutlinedIcon sx={{ width: "106px", height: "106px", color: "#CFD8DC" }} />
            <Typography sx={{ fontWeight: 600, fontSize: "16px", color: T.inkSoft }}>
              {translate.t("no_in_transit")}
            </Typography>
          </Box>
        ) : (
          <Box>
            {data?.primary?.map((group) => renderGroup(group, T.used))}
            {data?.alternatives?.map((group) => renderGroup(group, T.left))}
          </Box>
        )}
      </DialogContent>

      {!emptyOrError && (
        <Foot label={translate.t("total_in_transit_label")} value={`${formatValue(data?.total)} ${translate.t("unit")}`} />
      )}
    </Dialog>
  );
};

export default DialogResultInTransit;
